import React from "react";
import { STYLES, C, Section } from "../ffs/FfsSubpage";
import { esHref } from "../../i18n";
import GoldRule from "../../components/GoldRule";

/* ─── A provider's page, in Spanish ───
   The Spanish twin of src/pages/ProviderPage.js, rendered at /es/equipo/<slug> by ProviderEsRoute in src/App.js.
   The words come from the provider's `es` entry in src/providers.js; anything missing there falls back to the
   English entry. Same layout and styles as the English page.

   Nothing in this file may read `window` or `document` while rendering, or the publish-time render breaks. */

const PHONE_DISPLAY = "(212) 222-4628";
const PHONE_HREF = "tel:2122224628";
const EMAIL = "info@ovadiaps.com";

const EXTRA_STYLES = `
  .pv-hero-grid { display: grid; grid-template-columns: 1fr 280px; gap: clamp(32px, 5vw, 64px); align-items: end; }
  .pv-photo { margin: 0; }
  .pv-photo img { width: 100%; height: auto; display: block; border-top: 3px solid ${C.gold}; }
  .pv-initials { aspect-ratio: 4 / 5; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.06); border-top: 3px solid ${C.gold}; font-family: var(--head); font-size: clamp(56px, 8vw, 88px); color: ${C.gold}; letter-spacing: 4px; }
  .pv-role { font-family: var(--body); font-size: 13px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: ${C.gold}; margin-bottom: 14px; }
  .pv-two { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(28px, 4vw, 48px); align-items: start; }
  .pv-list { list-style: none; margin-top: 8px; }
  .pv-list li { font-family: var(--body); font-size: 15.5px; line-height: 1.55; color: ${C.textBody}; padding: 12px 0; border-top: 1px solid ${C.border}; }
  .pv-list li span { display: block; color: ${C.textLight}; font-size: 13.5px; margin-top: 2px; }
  .pv-list li a { color: ${C.navy}; font-weight: 600; text-decoration: underline; text-decoration-color: ${C.gold}; text-decoration-thickness: 1.5px; text-underline-offset: 4px; }
  .pv-sub { font-family: var(--body); font-size: 12.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: ${C.goldDark}; margin: 0 0 6px; }
  .pv-sub + .pv-sub, .pv-list + .pv-sub { margin-top: 32px; }
  @media (max-width: 860px) {
    .pv-hero-grid, .pv-two { grid-template-columns: 1fr; }
    .pv-photo { max-width: 240px; }
  }
`;

/* English field, overlaid with the Spanish one; list items are matched by position. */
const mergeList = (en = [], es = []) => en.map((item, i) => ({ ...item, ...(es[i] || {}) }));

export default function ProviderPageEs({ provider: p }) {
  const es = p.es || {};
  const role = es.role || p.role;
  const bio = es.bio || p.bio;
  const focus = es.focus || p.focus || [];
  const education = mergeList(p.education, es.education);
  const credentials = mergeList(p.credentials, es.credentials);
  const languages = es.languages || p.languages || [];
  const about = esHref("/about-dr-ovadia");
  const initials = `${p.firstName[0]}${p.lastName[0]}`;
  const rest = bio.slice(1);
  return (
    <main id="main-content" className="fs" style={{ background: C.bg }}>
      <style>{STYLES + EXTRA_STYLES}</style>

      <header className="fs-hero">
        <div className="fs-wrap">
          <div className="pv-hero-grid">
            <div>
              <p className="fs-crumb"><a href={about}>Nosotros</a> &nbsp;/&nbsp; <a href={`${about}#team`}>El equipo</a></p>
              <h1>{p.name}</h1>
              <div className="fs-stripe" aria-hidden="true"><span style={{ background: C.gold }} /></div>
              <p className="pv-role">{role}{p.joined ? ` · desde ${p.joined}` : ""}</p>
              <p className="fs-hero-lede">{bio[0]}</p>
              <div className="fs-btns">
                <a className="fs-btn fs-btn-gold" href="#consult">Solicite una consulta</a>
                <a className="fs-btn fs-btn-line" href={about}>Sobre el Dr. Ovadia</a>
              </div>
            </div>
            <figure className="pv-photo">
              {p.photo
                ? <img src={p.photo} width="800" height="1000" alt={es.photoAlt || p.photoAlt || p.name} />
                : <div className="pv-initials" role="img" aria-label={p.name}>{initials}</div>}
            </figure>
          </div>
        </div>
      </header>

      {rest.length > 0 && (
        <Section id="about" eyebrow="Perfil" title={`Sobre ${p.firstName}`} intro={rest} />
      )}

      {focus.length > 0 && (
        <Section id="role" eyebrow="En la práctica" title={`El papel de ${p.firstName}`} alt narrow={false}>
          <div className={`fs-cards${focus.length === 3 ? " fs-cards-3" : ""}`}>
            {focus.map((f) => (
              <div key={f.title} className="fs-card"><h3>{f.title}</h3><p>{f.text}</p></div>
            ))}
          </div>
        </Section>
      )}

      <Section id="training" eyebrow="Formación" title="Educación y credenciales">
        <div className="pv-two">
          <div>
            {education.length > 0 && (
              <>
                <p className="pv-sub">Educación</p>
                <ul className="pv-list">
                  {education.map((e) => <li key={e.what}>{e.what}<span>{e.where}{e.year ? `, ${e.year}` : ""}</span></li>)}
                </ul>
              </>
            )}
            {languages.length > 0 && (
              <>
                <p className="pv-sub">Idiomas</p>
                <ul className="pv-list"><li>{languages.join(" y ")}</li></ul>
              </>
            )}
          </div>
          <div>
            {credentials.length > 0 && (
              <>
                <p className="pv-sub">Certificación y licencias</p>
                <ul className="pv-list">
                  {credentials.map((c) => <li key={c.name}>{c.name}<span>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer">{c.by}</a> : c.by}</span></li>)}
                </ul>
              </>
            )}
            {p.memberships && p.memberships.length > 0 && (
              <>
                <p className="pv-sub">Afiliaciones</p>
                <ul className="pv-list">
                  {p.memberships.map((m) => <li key={m.name}>{m.url ? <a href={m.url} target="_blank" rel="noopener noreferrer">{m.name}</a> : m.name}</li>)}
                </ul>
              </>
            )}
          </div>
        </div>
        <p className="fs-note fs-narrow">{p.firstName} trabaja junto al <a href={about}>Dr. Steven Ovadia</a>, cirujano plástico certificado, quien planifica y dirige la cirugía de cada paciente de la práctica.</p>
      </Section>

      <section id="consult" className="fs-sec fs-cta">
        <div className="fs-wrap">
          <p className="fs-eyebrow">Próximo paso</p>
          <h2 className="fs-h2">Empiece con una consulta</h2>
          <GoldRule className="fs-rule" align="center" />
          <p className="fs-p fs-narrow" style={{ margin: "0 auto" }}>
            Según su procedimiento, es posible que durante el proceso se reúna tanto con {p.firstName} como con el Dr. Ovadia. Su cirugía se planifica con el Dr. Ovadia.
          </p>
          <div className="fs-cta-links">
            <a className="fs-btn fs-btn-gold" href={PHONE_HREF}>Llame al {PHONE_DISPLAY}</a>
            <a className="fs-btn fs-btn-navy" href={esHref("/#contact")}>Envíe un mensaje</a>
          </div>
          <p className="fs-offices">
            225 Broadway, Suite 3100, New York, NY<br />
            377 Jersey Ave, Suite 440, Jersey City, NJ · 90 Millburn Ave, Suite 206, Millburn, NJ<br />
            Consultas por teléfono y video · <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
          </p>
        </div>
      </section>
    </main>
  );
}
